

import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { IoArrowUndoSharp } from "react-icons/io5";
import { CommonContext } from "../../../App";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import ContactMarquee from "../../../Components/CommonComponent/Table/ContactMarquee";
import api from "../../../services/api";

export default function SubGroupsTable() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();


  const { ledger, entry } = location.state || {};


  const selectedGroupType = ledger?.groupType || entry?.type;
  const selectedGroupName = ledger?.groupName || entry?.group_name;
  const selectedGroupNameInHindi = ledger?.groupNameInHindi || entry?.groupNameInHindi;
  const selectedSubGroupName = ledger?.subGroupName || entry?.sub_group_name;

  const { branchId, dateRange, setDateRange } = useContext(CommonContext);

  const [tableData, setTableData] = useState([]);
  const [closingBalance, setClosingBalance] = useState(0);
  const [closingType, setClosingType] = useState("");
  const [totalDebitSum, setTotalDebitSum] = useState(0);
  const [totalCreditSum, setTotalCreditSum] = useState(0);
  const [totalOpeningSum, setTotalOpeningSum] = useState(0);
  const [totalOpeningQtySum, setTotalOpeningQtySum] = useState(0);
  const [totalCurrentQtySum, setTotalCurrentQtySum] = useState(0);
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const [loading, setLoading] = useState(false);
  const primeLanguage = localStorage.getItem("selectedLanguage");
  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const containerRef = useRef(null);
  const showQuantity = selectedGroupType !== "Assets" && selectedGroupType !== "Liability";
  // auto focus on load
  useEffect(() => {
    containerRef.current?.focus();
  }, []);
  const handleTableKeyDown = (e) => {
    if (!tableData.length) return;

    // ⬇️ DOWN
    if (e.key === "ArrowDown") {
      e.preventDefault();

      setFocusedRowIndex((prev) => {
        if (prev === null) return 0;
        return Math.min(prev + 1, tableData.length - 1);
      });
    }

    // ⬆️ UP
    if (e.key === "ArrowUp") {
      e.preventDefault();

      setFocusedRowIndex((prev) => {
        if (prev === null) return tableData.length - 1;
        return Math.max(prev - 1, 0);
      });
    }

    // SPACE → highlight
    if (e.key === " ") {
      e.preventDefault();
      if (focusedRowIndex === null) return;

      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(focusedRowIndex)) {
          newSet.delete(focusedRowIndex);
        } else {
          newSet.add(focusedRowIndex);
        }
        return newSet;
      });
    }

    // ENTER → navigate
    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedRowIndex !== null) {
        handleRowClick(tableData[focusedRowIndex]);
      }
    }

    // ESC → back
    if (e.key === "Escape") {
      e.preventDefault();
      navigate("/account-books/groups-table", { state: { typeName: selectedGroupType } });
    }
  };

  useLayoutEffect(() => {
    if (focusedRowIndex === null) return;

    const container = containerRef.current;
    const row = document.getElementById(`row-${focusedRowIndex}`);

    if (!container || !row) return;

    const containerHeight = container.clientHeight;
    const scrollTop = container.scrollTop;

    const rowTop = row.offsetTop;
    const rowHeight = row.offsetHeight;

    const rowCenter = rowTop + rowHeight / 2;
    const containerCenter = scrollTop + containerHeight / 2;

    const OFFSET = 60;

    if (rowCenter > containerCenter + OFFSET) {
      container.scrollTo({
        top: rowTop - containerHeight / 2 + rowHeight,
        behavior: "smooth",
      });
    } else if (rowCenter < containerCenter - OFFSET) {
      container.scrollTo({
        top: rowTop - containerHeight / 2,
        behavior: "smooth",
      });
    }
  }, [focusedRowIndex]);

  // reset when data changes
  useEffect(() => {
    setFocusedRowIndex(null);
    setSelectedRows(new Set());
  }, [tableData]);

  // click se focus
  useEffect(() => {
    const el = containerRef.current;
    const handleClick = () => el?.focus();

    el?.addEventListener("click", handleClick);
    return () => el?.removeEventListener("click", handleClick);
  }, []);
  useEffect(() => {
    if (!selectedGroupType || !selectedGroupName) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [ledgersRes, summaryRes] = await Promise.all([
          api.get(`ledgers/get-all-ledger?branchId=${branchId}`),
          api.get(`ledgers/get-balance-sheet`, {
            params: { startDate, endDate, branchId },
          }),
        ]);

        if (ledgersRes.data.success && summaryRes.data.success) {
          const allLedgers = ledgersRes.data.allData;
          const allSummary = summaryRes.data.data.flatMap(
            (branch) => branch.branchData || []
          );


          const filteredLedgers = allLedgers.filter(
            (item) =>
              item.groupType === selectedGroupType &&
              item.groupName === selectedGroupName
          );

          // Unique sub-groups
          const groupMap = new Map();
          filteredLedgers.forEach((item) => {
            const key = item.subGroupName || item.groupName;
            if (!groupMap.has(key)) {
              groupMap.set(key, { ...item, displayName: key });
            }
          });


          const uniqueGroups = Array.from(groupMap.values());

          const mappedData = uniqueGroups.map((sub) => {
            const summaries = allSummary.filter(
              (s) =>
                s.groupType === selectedGroupType &&
                s.groupName === selectedGroupName &&
                (s.subGroupName || s.groupName) === sub.displayName
            );
            const openingQuantity = summaries.reduce(
              (sum, s) => sum + Math.abs(s.prevQuantity || 0),
              0
            );

            const currentQuantity = summaries.reduce(
              (sum, s) => sum + Math.abs(s.totalQuantity || 0),
              0
            );




            // Opening balance
            const openingReceived = summaries.reduce(
              (sum, s) => sum + Math.abs(s.openingReceived || 0),
              0
            );
            const openingPaid = summaries.reduce(
              (sum, s) => sum + Math.abs(s.openingPaid || 0),
              0
            );

            const openingBalanceValue = Math.abs(openingReceived - openingPaid);
            const openingType =
              openingReceived > openingPaid ? "Dr" : "Cr";

            const showOpening =
              selectedGroupType === "Expense" || selectedGroupType === "Income"
                ? "-"
                : `${openingBalanceValue} ${openingType}`;


            // Debit/Credit
            const totalDebit = summaries.reduce(
              (sum, s) => sum + Math.abs(s.currentReceived || 0),
              0
            );
            const totalCredit = summaries.reduce(
              (sum, s) => sum + Math.abs(s.currentPaid || 0),
              0
            );

            // Closing balance
            let closingBalanceValue;
            let closingTypeVal;

            if (["Assets", "Liability"].includes(selectedGroupType)) {
              const openingSigned = openingReceived - openingPaid;
              const netChange = totalDebit - totalCredit;
              const closingSigned = openingSigned + netChange;

              closingBalanceValue = Math.abs(closingSigned);
              closingTypeVal = closingSigned >= 0 ? "Dr" : "Cr";
            } else {
              closingBalanceValue = Math.abs(totalDebit - totalCredit);
              closingTypeVal = totalDebit >= totalCredit ? "Dr" : "Cr";
            }

            return {
              ...sub,
              openingBalanceValue,
              openingType,
              showOpening,
              totalDebit,
              totalCredit,
              totalClosing: closingBalanceValue,
              closingType: closingTypeVal,
              openingQuantity,
              currentQuantity,
            };
          });

          const nonZeroData = mappedData.filter(
            (row) =>
              row.openingBalanceValue !== 0 ||
              row.totalDebit !== 0 ||
              row.totalCredit !== 0 ||
              row.totalClosing !== 0
          );

          setTableData(nonZeroData);

          // Totals
          const debitSum = nonZeroData.reduce((sum, r) => sum + r.totalDebit, 0);
          const creditSum = nonZeroData.reduce(
            (sum, r) => sum + r.totalCredit,
            0
          );
          const totalOpeningQty = nonZeroData.reduce(
            (sum, r) => sum + (r.openingQuantity || 0),
            0
          );

          const totalCurrentQty = nonZeroData.reduce(
            (sum, r) => sum + (r.currentQuantity || 0),
            0
          );
          setTotalOpeningQtySum(totalOpeningQty);
          setTotalCurrentQtySum(totalCurrentQty);

          setTotalDebitSum(debitSum);
          setTotalCreditSum(creditSum);

          if (["Assets", "Liability"].includes(selectedGroupType)) {
            const openingTotalSigned = nonZeroData.reduce((sum, r) => {
              const sign = r.openingType === "Dr" ? 1 : -1;
              return sum + r.openingBalanceValue * sign;
            }, 0);

            setTotalOpeningSum(Math.abs(openingTotalSigned));
          } else {
            setTotalOpeningSum(0);
          }

          const closingSignedTotal = nonZeroData.reduce((sum, r) => {
            const sign = r.closingType === "Dr" ? 1 : -1;
            return sum + r.totalClosing * sign;
          }, 0);

          setClosingBalance(Math.abs(closingSignedTotal));
          setClosingType(closingSignedTotal >= 0 ? "Dr" : "Cr");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    if (startDate && endDate && branchId) {

      fetchData();

    }
  }, [selectedGroupName, selectedGroupType, startDate, endDate, branchId]);

  const handleRowClick = (row) => {
    navigate("/account-books/ledgers-table", {
      state: {
        ledger: row,
      },
    });
  };

  return (
    <div className="container-fluid">
      <div className="row  ">
        <div className=" py-1 p-0 mt-2 sticky top-0">
          <NavLink
            to="/account-books/groups-table"
            title="Back to groups table"
            state={{ typeName: selectedGroupType }}
            className="inline-block p-1 px-2 text-theme-backBtn border-1 border-theme-backBtn rounded cursor-pointer hover:bg-theme-backBtn hover:text-theme-color"

          >
            <IoArrowUndoSharp />
          </NavLink>
        </div>

        <div className="flex justify-between py-[2px] bg-theme-headerBg text-theme-color px-2 text-sm ">
          <div className="text font-medium py-1">
            {/* Sub-Groups of {selectedGroupName}
            Sub-Groups of {selectedGroupNameInHindi} */}
            {/* {primeLanguage === "en" || primeLanguage === "pn"
                      ?`${t("subGroupsOf")} ${selectedGroupName} (${selectedGroupNameInHindi})`
                      :  `${selectedGroupNameInHindi} ${t("subGroupsOf")}`  
                    } */}
            {
              primeLanguage === "hi"
                ? `${selectedGroupNameInHindi} ${t("subGroupsOf")}`
                : `${t("subGroupsOf")} ${selectedGroupName}${primeLanguage === "en"
                  ? ` (${selectedGroupNameInHindi})`
                  : ""
                }`
            }
          </div>
        </div>

        <div
          ref={containerRef}
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          className="overflow-auto outline-none"
        >
          <table className="min-w-full bg-theme-tableBg text-sm  capitalize ">
            <thead>
              <tr className=" bg-white text-theme-headerBg border-tborder-1 border-theme-headerBg  ">
                <th className=" px-1 py-[2px] font-medium text-center border-b" >{t("srNumber")}</th>
                <th className=" px-1 font-medium text-start border-b">{t("subGroupName")}</th>

                {selectedGroupType !== "Expense" &&
                  selectedGroupType !== "Income" && (
                    <th className="  px-1 font-medium border-b text-end">
                      {t("openingBalance")}
                    </th>
                  )}
                {/* {showQuantity && (
                    <>
                      <th className="border px-1 font-medium text-center">{t("openingQty")}</th>
                      <th className="border px-1 font-medium text-center">{t("closingQty")}</th>
                    
                    </>
                  )} */}

                <th className="  px-1 font-medium text-end border-b">{t("receivedDr")}</th>
                <th className=" px-1 font-medium text-end border-b">{t("paidCr")}</th>

                <th className="  px-1 font-medium text-end border-b">
                  {selectedGroupType !== "Expense" &&
                    selectedGroupType !== "Income"
                    ? t("closingBalance")
                    : t("total")}
                </th>
              </tr>
            </thead>

            {!loading && (
              <>
                <tbody>
                  {tableData.length > 0 ? (
                    tableData.map((row, i) => (
                      <tr
                        id={`row-${i}`}
                        key={i}
                        onClick={() => {
                          setFocusedRowIndex(i); // ✅ click se focus
                          handleRowClick(row);
                        }}
                        className={`
                          cursor-pointer transition-colors duration-150
                          ${focusedRowIndex === i
                            ? "bg-[#5c5b5b] text-white "       //  FOCUS (priority high)
                            : selectedRows.has(i)
                              ? "bg-[#f4c48a]"     //  SELECTED (fallback)
                              : ""
                          }
  `}
                      >
                        <td className="border-b px-1 text-center">{i + 1}</td>
                        <td className="border-b px-1 ">
                          {/* {row.displayName} */}
                          <ContactMarquee firstName={row.displayName} secondName={row.subGroupNameInHindi} />
                        </td>

                        {selectedGroupType !== "Expense" &&
                          selectedGroupType !== "Income" && (
                            <td className="border-b px-1 text-end">{row.showOpening}</td>
                          )}


                        <td className="border-b py-[2px] px-1 text-end">
                          {row.totalDebit.toLocaleString()}
                        </td>
                        <td className="border-b px-1 text-end">
                          {row.totalCredit.toLocaleString()}
                        </td>
                        <td className="border-b px-1 text-end">
                          {row.totalClosing.toLocaleString()}{" "}
                          {selectedGroupType !== "Expense" &&
                            selectedGroupType !== "Income" &&
                            row.closingType}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className=" px-4 py-5 text-center text-gray-500 text-sm "
                      >
                        {t("noDataAvailable")}
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot>
                  <tr className=" bg-white  border-theme-headerBg ">
                    <td
                      colSpan="2"
                      className="border-b  text-start ps-3 font-semibold px-1"
                    >
                      {t("total")}:
                    </td>

                    {selectedGroupType !== "Expense" &&
                      selectedGroupType !== "Income" && (
                        <td className="border-b  font-semibold  px-1 text-end">
                          {totalOpeningSum.toLocaleString()} {closingType}
                        </td>
                      )}
                    {/* {showQuantity && (
                        <>
                          <td className="border px-1 text-end font-medium">
                            {totalOpeningQtySum.toLocaleString()}
                          </td>
                          <td className="border px-1 text-end font-medium">
                            {totalCurrentQtySum.toLocaleString()}
                          </td>
                        </>
                      )} */}

                    <td className="border-b py-[2px] px-1 text-end  font-semibold">
                      {totalDebitSum.toLocaleString()}
                    </td>
                    <td className="border-b px-1 text-end  font-semibold">
                      {totalCreditSum.toLocaleString()}
                    </td>

                    <td className="border-b  px-1  font-semibold text-end">
                      {closingBalance.toLocaleString()}{" "}
                      {selectedGroupType !== "Expense" &&
                        selectedGroupType !== "Income" &&
                        closingType}
                    </td>
                  </tr>
                </tfoot>
              </>
            )}
          </table>
        </div>

        {loading && (
          <div className="flex justify-center my-4">
            <LoadingSpinner size={70} color="#000000" />
          </div>
        )}
      </div>
    </div>
  );
}










